import { convertSquareMetersToSquareFeet } from "@/components/common/settings-context/distance-unit-utils";
import {
  CircularProgress,
  FaroCheckbox,
  FaroText,
  grey,
} from "@faro-lotv/flat-ui";
import { Box, ButtonBase, Divider, Stack } from "@mui/material";
import {
  Fragment,
  useEffect,
  type JSX,
  type PointerEvent,
  type ReactNode,
} from "react";
import { selectIsolatedFloor, useFloorAreaStore } from "./floor-area-store";
import { FLOOR_COLORS } from "./use-floor-coloring";

/** Height above the lowest level, signed */
const HEIGHT_FORMAT = new Intl.NumberFormat(undefined, {
  signDisplay: "exceptZero",
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/** @returns the plan area of each floor, highest first, with the option to color the floors in 3D */
export function FloorAreaPanel(): JSX.Element {
  const { isSampling, sampledPoints, estimate, isColoredByFloors, error } =
    useFloorAreaStore();
  const setColoredByFloors = useFloorAreaStore(
    (state) => state.setColoredByFloors,
  );
  const isolatedFloor = useFloorAreaStore(selectIsolatedFloor);
  const pinnedFloor = useFloorAreaStore((state) => state.pinnedFloor);
  const { setHoveredFloor, togglePinnedFloor } = useFloorAreaStore.getState();
  const levels = estimate?.levels ?? [];

  useEffect(
    () => () =>
      useFloorAreaStore.setState({
        hoveredFloor: undefined,
        pinnedFloor: undefined,
      }),
    [],
  );

  let status: ReactNode;
  if (error) {
    status = (
      <FaroText variant="bodyS" color="error.main">
        {error}
      </FaroText>
    );
  } else if (!estimate) {
    status = (
      <Stack
        direction="row"
        role="status"
        sx={{ alignItems: "center", gap: 1 }}
      >
        <CircularProgress size={16} />
        <FaroText variant="bodyS" color={grey[600]}>
          {isSampling
            ? `Loading points… ${sampledPoints.toLocaleString()}`
            : "Measuring the floors…"}
        </FaroText>
      </Stack>
    );
  } else if (!levels.length) {
    status = (
      <FaroText variant="bodyS" color={grey[600]}>
        No walkable surface found in the selection.
      </FaroText>
    );
  }

  return (
    <Stack sx={{ gap: 1 }}>
      <FaroText variant="labelM">Floors</FaroText>
      <Stack
        component="ul"
        sx={{
          m: 0,
          p: 0,
          listStyle: "none",
          borderRadius: 1,
          border: `1px solid ${grey[200]}`,
          overflow: "hidden",
        }}
      >
        {status && <Box sx={{ p: 1.5 }}>{status}</Box>}
        {levels
          .map((level, index) => ({ level, index }))
          .reverse()
          .map(({ level, index }, row) => (
            <Fragment key={level.elevation}>
              {row > 0 && <Divider component="li" aria-hidden />}
              <FloorRow
                color={FLOOR_COLORS[index % FLOOR_COLORS.length]}
                name={index ? `Level ${index}` : "Lowest level"}
                detail={
                  index
                    ? `${HEIGHT_FORMAT.format(level.elevation - levels[0].elevation)} m`
                    : "Indoor and outdoor ground"
                }
                areaM2={level.areaM2}
                isIsolated={isolatedFloor === index}
                isPinned={pinnedFloor === index}
                onHover={(isHovered) =>
                  setHoveredFloor(isHovered ? index : undefined)
                }
                onClick={() => togglePinnedFloor(index)}
              />
            </Fragment>
          ))}
      </Stack>
      {!!levels.length && (
        <FaroCheckbox
          checked={isColoredByFloors}
          onChange={(_, checked) => setColoredByFloors(checked)}
          label="Color by floors"
          controlSx={{ m: 0 }}
        />
      )}
    </Stack>
  );
}

type FloorRowProps = {
  /** Color of the floor in 3D */
  color: string;
  name: string;
  /** Where the floor is */
  detail: string;
  areaM2: number;
  /** Shown alone in 3D */
  isIsolated: boolean;
  /** Kept alone in 3D after a click or tap */
  isPinned: boolean;
  onHover(isHovered: boolean): void;
  onClick(): void;
};

/** @returns a floor with its plan area in square meters and feet, isolating the floor in 3D on hover or click */
function FloorRow({
  color,
  name,
  detail,
  areaM2,
  isIsolated,
  isPinned,
  onHover,
  onClick,
}: FloorRowProps): JSX.Element {
  // Touch screens emulate the mouse on tap, which would leave a floor hovered
  function hoverWithMouse(event: PointerEvent): void {
    if (event.pointerType === "mouse") onHover(event.type === "pointerenter");
  }
  return (
    <Box component="li">
      <ButtonBase
        aria-pressed={isPinned}
        onPointerEnter={hoverWithMouse}
        onPointerLeave={hoverWithMouse}
        onClick={onClick}
        sx={[
          {
            width: "100%",
            textAlign: "left",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 1.5,
            px: 1.5,
            py: 1,
          },
          isIsolated && { backgroundColor: grey[100] },
          isPinned && { backgroundColor: grey[200] },
        ]}
      >
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "2px",
            flexShrink: 0,
            backgroundColor: color,
          }}
        />
        <Stack sx={{ flexGrow: 1 }}>
          <FaroText variant="labelM">{name}</FaroText>
          <FaroText variant="bodyS" color={grey[600]}>
            {detail}
          </FaroText>
        </Stack>
        <Stack sx={{ alignItems: "flex-end" }}>
          <FaroText variant="labelM">
            {`${Math.round(areaM2).toLocaleString()} m\u00B2`}
          </FaroText>
          <FaroText variant="bodyS" color={grey[600]}>
            {`${Math.round(convertSquareMetersToSquareFeet(areaM2)).toLocaleString()} ft\u00B2`}
          </FaroText>
        </Stack>
      </ButtonBase>
    </Box>
  );
}
